/**
 * Action 配置块 JSON Schema 定义
 */
export const actionSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/action.json',
  title: 'Action 配置',
  description: 'Action（动作）配置数据结构',
  type: 'object',
  required: ['action_id', 'name', 'action_type'],
  properties: {
    action_id: {
      type: 'string',
      title: 'Action ID',
      description: 'Action 唯一标识符',
      pattern: '^action_[a-zA-Z0-9_]+$',
      minLength: 8,
      maxLength: 50,
      examples: ['action_attack', 'action_talk', 'action_investigate'],
    },
    name: {
      type: 'string',
      title: '动作名称',
      description: '动作的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['攻击', '对话', '调查', '使用物品'],
    },
    description: {
      type: 'string',
      title: '动作描述',
      description: '动作的详细描述',
      minLength: 0,
      maxLength: 500,
    },
    action_type: {
      type: 'string',
      title: '动作类型',
      enum: ['攻击', '对话', '移动', '交互', '使用物品', '技能', '其他'],
    },
    target_type: {
      type: 'string',
      title: '目标类型',
      description: '动作作用的目标类型',
      enum: ['NPC', '玩家', '物品', '地点', '自己', '无', '其他'],
    },
    parameters: {
      type: 'object',
      title: '参数',
      description: '动作所需的参数',
      properties: {
        range: {
          type: 'number',
          title: '范围',
          description: '动作的有效范围',
          minimum: 0,
        },
        cooldown: {
          type: 'integer',
          title: '冷却时间',
          description: '动作冷却时间（秒）',
          minimum: 0,
        },
        duration: {
          type: 'integer',
          title: '持续时间',
          description: '动作持续时间（秒）',
          minimum: 0,
        },
        cost: {
          type: 'object',
          title: '消耗',
          description: '执行动作所需的资源消耗',
          properties: {
            health: {
              type: 'integer',
              minimum: 0,
            },
            stamina: {
              type: 'integer',
              minimum: 0,
            },
            mana: {
              type: 'integer',
              minimum: 0,
            },
          },
        },
      },
    },
    effects: {
      type: 'array',
      title: '效果',
      description: '动作触发的效果',
      items: {
        type: 'object',
        required: ['effect_type'],
        properties: {
          effect_type: {
            type: 'string',
            enum: ['伤害', '治疗', '状态变化', '变量修改', '触发事件', '其他'],
          },
          value: {
            type: 'number',
            title: '效果值',
          },
          duration: {
            type: 'integer',
            title: '持续时间',
            minimum: 0,
          },
          condition: {
            type: 'string',
            title: '触发条件',
          },
        },
      },
    },
    requirements: {
      type: 'object',
      title: '执行要求',
      description: '执行动作的前置条件',
      properties: {
        min_level: {
          type: 'integer',
          title: '最低等级',
          minimum: 1,
        },
        required_items: {
          type: 'array',
          title: '所需物品',
          items: {
            type: 'string',
            pattern: '^item_[a-zA-Z0-9_]+$',
          },
        },
        required_state: {
          type: 'object',
          title: '所需状态',
          description: '玩家需要满足的状态条件',
          additionalProperties: {
            type: ['string', 'number', 'boolean'],
          },
        },
      },
    },
    icon: {
      type: 'string',
      title: '图标',
      description: '动作图标的资源路径',
      maxLength: 200,
    },
    animation: {
      type: 'string',
      title: '动画',
      description: '动作播放的动画资源路径',
      maxLength: 200,
    },
    sound_effect: {
      type: 'string',
      title: '音效',
      description: '动作的音效资源路径',
      maxLength: 200,
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type ActionConfig = {
  action_id: string
  name: string
  description?: string
  action_type: string
  target_type?: string
  parameters?: {
    range?: number
    cooldown?: number
    duration?: number
    cost?: {
      health?: number
      stamina?: number
      mana?: number
    }
  }
  effects?: Array<{
    effect_type: string
    value?: number
    duration?: number
    condition?: string
  }>
  requirements?: {
    min_level?: number
    required_items?: string[]
    required_state?: Record<string, string | number | boolean>
  }
  icon?: string
  animation?: string
  sound_effect?: string
  metadata?: Record<string, any>
}
